import { AgxSolicitorDetails } from '@urbanx/agx-ui-components';
import { Feature } from '@urbanx/agx.agencies.client';
import { AgxSpacer, AgxTypography, AgxVendorBuyers } from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  EditScope,
  SolicitorDetails,
  StakeholderType,
  VendorBuyerTypes,
  VendorModel,
} from '@urbanx/agx.campaigns.client';
import { useFeatureToggles } from 'hooks';

interface VendorDetailsSectionProps {
  campaign?: CampaignDetailModel | undefined;
  vendorDetails: VendorModel[];
  setVendorDetails: (value: VendorModel[]) => void;
  solicitorDetails: SolicitorDetails | undefined;
  setSolicitorDetails: (value: SolicitorDetails | undefined) => void;
  exportedIdentityVerificationSourceIds: string[];
  hasUnsavedChanges: boolean;
}

const VendorDetailsSection = ({
  campaign,
  vendorDetails,
  setVendorDetails,
  solicitorDetails,
  setSolicitorDetails,
  exportedIdentityVerificationSourceIds,
  hasUnsavedChanges,
}: VendorDetailsSectionProps) => {
  const { isEnabled } = useFeatureToggles();
  const hideIdentityVerification = !isEnabled(Feature.IdentityVerification);

  return (
    <AgxSpacer id="vendor-details" direction="vertical" gap="spacing-xl" fill>
      <Panel
        title={
          <AgxTypography id="vendorDetailsTitle" type="body" size="medium">
            Vendor Details
          </AgxTypography>
        }
        padding={{
          top: 'md',
          bottom: 'md',
          left: 'xl',
          right: 'xl',
        }}
      >
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxVendorBuyers
            key={`VendorDetails-${vendorDetails.length}`}
            id="vendorDetails"
            onChange={({ value }) => setVendorDetails(value as VendorModel[])}
            propertyAddress={campaign?.address}
            defaultValue={vendorDetails}
            editScope={EditScope.FullEdit}
            stakeholderType={StakeholderType.Vendor}
            vendorBuyerTypes={[
              VendorBuyerTypes.Individual,
              VendorBuyerTypes.PowerOfAttorney,
              VendorBuyerTypes.DeceasedEstate,
              VendorBuyerTypes.Company,
            ]}
            companyVendorBuyerTypes={[
              VendorBuyerTypes.Individual,
              VendorBuyerTypes.PowerOfAttorney,
              VendorBuyerTypes.DeceasedEstate,
              VendorBuyerTypes.Company,
            ]}
            vendorBuyerConfig={{
              preferredNameRequired: true,
              emailRequired: true,
              phoneNumberRequired: true,
              addressRequired: false,
              excludeMiddleName: false,
              displayAddCc: false,
              hideIdentityVerification,
              hideCompletedIdentityVerification: true,
              enableProofOfIdentity: campaign?.state === CampaignDetailModelState.NSW,
            }}
            powerOfAttorneyConfig={{
              hideDocumentationUpload: true,
            }}
            companyConfig={{
              hideAbn: false,
              hideAcn: false,
              hideRegisteredForGst: false,
              requireAcn: true,
              requireAbn: false,
            }}
            required
            campaignId={campaign?.campaignId || ''}
            displayLegalName={false}
            agents={[]}
            leadAgentId=""
            validate={hasUnsavedChanges}
            exportedIdentityVerificationSourceIds={exportedIdentityVerificationSourceIds}
          />
        </AgxSpacer>
      </Panel>
      <Panel
        title={
          <AgxTypography id="vendorSolicitorTitle" type="body" size="medium">
            Vendor Solicitor
          </AgxTypography>
        }
        padding={{
          top: 'md',
          bottom: 'md',
          left: 'xl',
          right: 'xl',
        }}
      >
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxSolicitorDetails
            key={`VendorSolicitorDetails-${solicitorDetails?.solicitorType}`}
            id="vendorSolicitorDetails"
            onValueUpdated={({ value }) => setSolicitorDetails(value)}
            defaultValue={solicitorDetails}
            label=""
            solicitorLabel="Solicitor"
            required
            validate={hasUnsavedChanges}
          />
        </AgxSpacer>
      </Panel>
    </AgxSpacer>
  );
};

export default VendorDetailsSection;
